import { gsap } from 'gsap';

import { Reel } from '../../core/classes/reel';
import { isMobile } from '../../core/helpers/isMobile';
import { isoStandard } from '../../core/helpers/isoStandard';
import { initGameLoopManager } from '../../core/helpers/gameLoop';
import { GAME_SPEED_TYPES } from '../../core/constants/gameConstants';
import { addTouchControl } from '../../core/classes/touchSpinControll';
import { amountContainerInfo } from '../../core/constants/amountContainerInfo';
import { SpinIconNumberProvider } from '../../core/helpers/spinIconNumberProvider';
import { handleSymbolAnimationPositionInMatrix } from '../../core/helpers/createWinLinesMatrix';
import {
  iconsIdByName,
  iconStates,
  randomIconsGroup,
  slotBarSymbolNames,
} from '../../core/constants/symbolsNames';

import { gameStore } from '../../store/gameStore';
import { gambleStore } from '../../store/gambleStore';
import { winBarStore } from '../../store/winBarStore';
import { freeBetStore } from '../../store/freeBetStore';
import { userInfoStore } from '../../store/userInfoStore';
import { bettingPartStore } from '../../store/bettingPartStore';

import { gameService } from '../gameService';
import { winPopupShownMinOdd } from '../../gameConstants';
import { SoundTypes, soundService } from '../soundService';
import { gameResourcesService } from '../gameResourcesService';

let resetExpandWildSpineTimeoutId = null;
class GameDrawer {
  columns = [];

  iconsHeight = isMobile() ? 76 : 146;

  gameSpeed = 300;

  spinTimeOudId = [];

  isForceStop = false;

  isResponseEnded = false;

  columnsStopCount = 0;

  spinDirection = 1;

  expandEndedColumns = [];

  columnsMoveSpeed = isMobile() ? 28 : 36;

  randomSymbolGroupId = 0;

  startLoopCount = 7;

  scatter1Count = 0;

  scatter2Count = 0;

  calculateReelParams() {
    const rows = 4;
    const iconsDistance = 0;
    const iconsInReel = 4;
    const reverseLineOffset = 30;
    const { iconsHeight } = this;
    const finalPositions = isMobile() ? [0, 76, 152, 228] : [0, 146, 292, 438];
    const topLine = Math.floor(finalPositions[0] - iconsHeight / 2);
    const webColumnPositions = [110, 307, 505, 700, 895];
    const mobileColumnPositions = [0, 100, 198, 297, 395];
    const bottomLine = rows * iconsHeight + (rows - 1) * iconsDistance;
    const { columnsMoveSpeed } = this;

    return {
      iconsInReel,
      iconsHeight,
      iconsDistance,
      topLine,
      webColumnPositions,
      mobileColumnPositions,
      bottomLine,
      takeOffLineOffset: 30,
      reverseLineOffset,
      columnsMoveSpeed,
      iconMargins: { default: (iconsHeight + iconsDistance) / 2 },
      finalPositions,
    };
  }

  initGameReels(spinContainer, winContainer, ticker) {
    this.columns = [];
    const columnsCount = 5;
    initGameLoopManager(ticker);
    const reelParams = this.calculateReelParams();
    for (let columnNumber = columnsCount - 1; columnNumber >= 0; columnNumber -= 1) {
      const reel = new Reel(
        columnNumber,
        spinContainer,
        winContainer,
        reelParams,
        new SpinIconNumberProvider(columnNumber),
        columnNumber === columnsCount - 1
      );
      this.columns[columnNumber] = reel;
    }
    this.columns.forEach((col, index) => {
      col.fullUpdateFromResponseData(gameStore.gameInitialMatrix[index]);
    });
    gameResourcesService.setupPixiPlugins();
  }

  generateRandomSymbolType() {
    this.randomSymbolGroupId = Math.floor(Math.random() * randomIconsGroup.length);
  }

  startSpinAnimation() {
    this.generateRandomSymbolType();
    gameResourcesService.resetAllLineSpine();
    clearTimeout(resetExpandWildSpineTimeoutId);
    resetExpandWildSpineTimeoutId = setTimeout(() => {
      gameResourcesService.resetExpandWildSpine();
    }, 500);
    this.resetReelState();
    this.updateReelAnimationSpeed();
    this.columns.forEach((col) => {
      col.resetIcons();
      col.startLoopCount = this.startLoopCount;
      col.columnsMoveSpeed = this.columnsMoveSpeed;
      col.startSpin(this.spinDirection);
    });
    this.spinDirection = 1;
  }

  resetReelState() {
    this.startedColumns = 5;
    this.columnsStopCount = 0;
  }

  handleExpandingWildArray(expandingWild) {
    return expandingWild.reduce((acc, curr, index) => {
      if (curr) {
        acc.push(index);
      }
      return acc;
    }, []);
  }

  stopSpinAnimation() {
    const reelDelay = this.isForceStop ? 0 : this.gameSpeed;
    this.isResponseEnded = true;
    this.columns.forEach((col, index) => {
      const timeOutId = setTimeout(() => {
        col.startLoopCount = this.startLoopCount;
        col.stopSpin(this.isForceStop);
      }, index * reelDelay);
      this.spinTimeOudId.push(timeOutId);
    });
  }

  updateReelAnimationSpeed() {
    if (bettingPartStore.gameSpeedType === GAME_SPEED_TYPES.turbo) {
      this.gameSpeed = 0;
      this.columnsMoveSpeed = isMobile() ? 38 : 60;
      this.startLoopCount = 3;
    } else if (bettingPartStore.gameSpeedType === GAME_SPEED_TYPES.fast) {
      this.gameSpeed = 100;
      this.startLoopCount = 7;
      this.columnsMoveSpeed = isMobile() ? 32 : 40;
    } else {
      this.gameSpeed = 300;
      this.startLoopCount = 7;
      this.columnsMoveSpeed = isMobile() ? 28 : 36;
    }
  }

  forceStopAnimation() {
    this.isForceStop = true;
    this.startLoopCount = 1;
    if (this.isResponseEnded) {
      this.spinTimeOudId.forEach((timeOudId) => {
        clearTimeout(timeOudId);
      });
      this.columns.forEach((column) => {
        column.startLoopCount = 1;
        column.stopSpin(true);
      });
    }
  }

  handleScatter1Sound(reelIndex) {
    if (reelIndex <= 2) {
      this.scatter1Count += 1;
      soundService.play(SoundTypes.Scatter1Win);
    } else if (this.scatter1Count && reelIndex === 3) {
      this.scatter1Count += 1;
      soundService.play(SoundTypes.Scatter1Win);
    } else if (this.scatter1Count > 2 && reelIndex === 4) {
      soundService.play(SoundTypes.Scatter1Win);
    }
  }

  handleScatter2Sound(reelIndex) {
    if (reelIndex === 0) {
      this.scatter2Count += 1;
      soundService.play(SoundTypes.Scatter2Win);
    }
    if (!(reelIndex % 2) && reelIndex !== 0 && this.scatter2Count) {
      soundService.play(SoundTypes.Scatter2Win);
    }
  }

  resetScatterCount() {
    this.scatter1Count = 0;
    this.scatter2Count = 0;
  }

  handleReelSound(reelIndex) {
    soundService.play(SoundTypes.ReelStop);
    this.columns[reelIndex]._reelIcons.forEach((icon) => {
      if (icon.iconNumber === iconsIdByName[slotBarSymbolNames.icon9]) {
        soundService.play(SoundTypes.WildSymbol);
      } else if (icon.iconNumber === iconsIdByName[slotBarSymbolNames.icon10]) {
        this.handleScatter1Sound(reelIndex);
      } else if (icon.iconNumber === iconsIdByName[slotBarSymbolNames.icon11]) {
        this.handleScatter2Sound(reelIndex);
      }
    });
  }

  async handleStopReelAnimation() {
    this.columnsStopCount += 1;
    if (this.columnsStopCount === this.startedColumns) {
      this.isForceStop = false;
      this.isResponseEnded = false;
      const {
        spinResult: { additional = {}, winAmount, gambleInfo, freeBetInfo },
      } = gameStore;
      const { wins, expandingWild } = additional;

      await this.startWinAnimation({
        winAmount,
        gambleInfo,
        wins,
        expandingWild,
        freeBetInfo,
      });
    }
  }

  playWinCoinAnimation() {
    gameResourcesService.winCoinSpine.play('animation');
    gameResourcesService.winCoinSpine.addListener(
      'complete',
      () => {
        gameResourcesService.winCoinSpine.stop();
      },
      null,
      'animation'
    );
  }

  skipWinAnimation() {
    this.winAnimationTimeLine && this.winAnimationTimeLine.progress(1, true);
  }

  animateWinAmount({ value, duration = 0.8 }) {
    if (freeBetStore.currentFreeBet && !value) {
      return;
    }
    const { winAmountTexture } = gameResourcesService;
    gameResourcesService.winAmountTexture.text = '';
    gameResourcesService.winAmountTexture.textContent = '';
    this.winAnimationTimeLine = gsap.timeline();
    this.winAnimationTimeLine.to(winAmountTexture, {
      textContent: value,
      duration,
      ease: 'none',
      snap: { textContent: 0.01 },
      stagger: {
        each: 1.0,
        onUpdate: function () {
          winAmountTexture.text = isoStandard(+this.targets()[0].textContent, true);
        },
      },
      onComplete: function () {
        winAmountTexture.text = isoStandard(+value, true);
      },
    });
  }

  handleScatterWin(iconNumber) {
    this.columns.forEach((column) => {
      column._reelIcons.forEach((icon) => {
        if (icon.iconNumber === iconNumber) {
          icon.changeState(iconStates.win, true);
        } else {
          icon.changeState(iconStates.noWin);
        }
      });
    });
  }

  async startWinAnimation({
    winAmount,
    gambleInfo,
    freeBetInfo,
    wins = [],
    expandingWild = [0, 0, 0, 0, 0],
  }) {
    const scatterWinType = wins.find((el) => !el.line);
    if (scatterWinType) {
      this.handleScatterWin(scatterWinType.symbol);
    }
    if (expandingWild.some((el) => el)) {
      gameStore.setIsSpinStart(true);
      clearTimeout(resetExpandWildSpineTimeoutId);
      await this.startWildAnimation(expandingWild);
    }
    let winLinesData = [];

    gambleStore.setGambleInfo(gambleInfo);
    if (winAmount) {
      this.animateWinAmount({ value: winAmount });
      winBarStore.setWinAmount(winAmount);
      winBarStore.updateShowAmountContainerInfo(amountContainerInfo.winContainer, true);
      this.playWinCoinAnimation();
    }
    if (freeBetInfo && !freeBetInfo.reminingSpinCount) {
      winBarStore.setTotalWinAmount(freeBetInfo.totalWin);
      freeBetStore.setCurrentFreeBet({
        ...freeBetStore.currentFreeBet,
        freeBetInfo,
        remainingCount: freeBetInfo.reminingSpinCount,
      });
    }
    if (!userInfoStore.isReplay) {
      gameStore.setIsSpinStart(false);
    }

    if (wins.length) {
      winLinesData = handleSymbolAnimationPositionInMatrix(wins);
      await this.startAllWinLineAnimation(winLinesData);
    }
    gameResourcesService.resetAllLineSpine();

    gameService.spinFinished({
      winAmount,
      gambleInfo,
      odd: gameStore.odd,
    });
    if (!bettingPartStore.selectedAutoBetCount && winLinesData.length) {
      await this.startWinLineAnimation(winLinesData, 0);
    }
  }

  startWinLineAnimation(winLinesData, start) {
    return new Promise((resolve) => {
      const winLine = winLinesData[start];
      const { line, lines } = winLine;
      const lineIndex = line - 1;
      const isFromRight = line > 100;
      const animationName = isFromRight ? 'animation2' : 'animation';
      this.columns.forEach((column, i) => {
        column._reelIcons.forEach((icon, j) => {
          if (lines[j][i] === 1) {
            icon.changeState(iconStates.win);
          } else {
            icon.iconNumber === 9
              ? icon.changeState(iconStates.idle)
              : icon.changeState(iconStates.noWin);
          }
          if (lines[j][i] && line) {
            gameResourcesService.allWinLinesBone[lineIndex][isFromRight ? 4 - i : i].y =
              -icon.y + (isMobile() ? 100 : 196);
          }
        });
      });
      winBarStore.setWinLine(line);
      if (line) {
        if (gameStore.odd <= winPopupShownMinOdd) {
          soundService.play(SoundTypes.WinOneLineSound);
        }
        gameResourcesService.allLinesSpine[lineIndex].play(animationName);
        gameResourcesService.allLinesSpine[lineIndex].addListener(
          'complete',
          () => {
            start += 1;
            start = start > winLinesData.length - 1 ? 0 : start;
            this.startWinLineAnimation(winLinesData, start);
            resolve();
          },
          null,
          animationName
        );
      } else {
        gameResourcesService.winLineTimeOutId = setTimeout(() => {
          start += 1;
          start = start > winLinesData.length - 1 ? 0 : start;
          this.startWinLineAnimation(winLinesData, start);
          resolve();
          clearTimeout(gameResourcesService.winLineTimeOutId);
        }, 1000);
      }
    });
  }

  startAllWinLineAnimation(winLinesData) {
    return new Promise((resolve) => {
      let resolverCount = 0;
      soundService.play(SoundTypes.WinningLineSound);
      winLinesData.forEach(({ line, allLines, lines }) => {
        const lineIndex = line - 1;
        const isFromRight = line > 100;
        const animationName = isFromRight ? 'animation2' : 'animation';
        this.columns.forEach((column, i) => {
          column._reelIcons.forEach((icon, j) => {
            if (allLines[j][i] === 1) {
              icon.changeState(iconStates.win);
            } else {
              icon.iconNumber === 9
                ? icon.changeState(iconStates.idle, true)
                : icon.changeState(iconStates.noWin);
            }
            if (lines[j][i] && line) {
              gameResourcesService.allWinLinesBone[lineIndex][isFromRight ? 4 - i : i].y =
                -icon.y + (isMobile() ? 98 : 194);
            }
          });
        });
        if (line) {
          gameResourcesService.allLinesSpine[lineIndex].play(animationName, true);
          gameResourcesService.allLinesSpine[lineIndex].addListener(
            'complete',
            () => {
              resolverCount += 1;
              if (resolverCount === winLinesData.length) {
                resolve();
              }
            },
            null,
            animationName
          );
        } else {
          resolverCount += 1;
          if (resolverCount === winLinesData.length) {
            resolve();
          }
        }
      });
    });
  }

  play2Or3WildWinAnimation(
    expandingWild,
    spine,
    index,
    animationName,
    idleAnimation,
    promiseResolve
  ) {
    spine[index].isOpen = true;
    spine[index].play(animationName, false);
    this.addShakeEventAfterWildWin(spine[index], animationName, expandingWild, promiseResolve);
    spine[index].addListener(
      'complete',
      () => {
        spine[index].play(idleAnimation, true);
        promiseResolve();
      },
      null,
      animationName
    );
  }

  handle2Or3WildWinAnimation(winWildSpine, expandingWild, promiseResolve, winExpandWildIndexes) {
    const count = winExpandWildIndexes.length;
    winWildSpine.forEach((wild) => {
      wild.y = 0;
    });
    if (count === 2 && expandingWild[winExpandWildIndexes[0] + 1] === 9) {
      this.play2Or3WildWinAnimation(
        expandingWild,
        winWildSpine,
        winExpandWildIndexes[0],
        'wild5',
        'idleBig5',
        promiseResolve
      );
      soundService.play(SoundTypes.WildOpeningSound2);
    } else if (count === 3) {
      this.play2Or3WildWinAnimation(
        expandingWild,
        winWildSpine,
        2,
        'wild6',
        'idleBig6',
        promiseResolve
      );
      soundService.play(SoundTypes.WildOpeningSound3);
    } else {
      promiseResolve();
    }
  }

  handleShakeAnimationType(expandingWild) {
    const { length } = this.expandingWildsArr;
    const nextWild = expandingWild[this.expandingWildsArr[0] + 1];
    const shakeType =
      length === 2 && nextWild === 9 ? 'shake2' : length === 3 ? 'shake3' : 'shake1';
    gameResourcesService.shakeSpine.play(shakeType);
    soundService.play(SoundTypes.SceneShakeSound);
    !isMobile() && gameResourcesService.dustSpine.play('dust');
  }

  addShakeEventAfterWildWin(spine, animationName, expandingWild, resolve) {
    spine.addListener(
      'ivent',
      () => {
        this.handleShakeAnimationType(expandingWild);
        resolve();
      },
      null,
      animationName
    );
  }

  startWildAnimation(expandingWild) {
    return new Promise((resolve) => {
      const expandingWildsArr = this.handleExpandingWildArray(expandingWild);
      this.expandingWildsArr = expandingWildsArr;
      gameStore.setIsExpandWildOpen(true);
      if (expandingWildsArr.length > 1 && expandingWild[expandingWildsArr[0] + 1] === 9) {
        this.handle2Or3WildWinAnimation(
          gameResourcesService.expandWildSpine,
          expandingWild,
          resolve,
          expandingWildsArr
        );
        return;
      }

      this.columns.forEach((reel) => {
        if (expandingWildsArr.includes(reel.index)) {
          reel._reelIcons.forEach((symbol) => {
            if (symbol.iconNumber === 9) {
              if (symbol.y < (isMobile() ? 76 : 146)) {
                gameResourcesService.expandWildSpine[reel.index].play('wild1', false);
                gameResourcesService.expandWildSpine[reel.index].y = symbol.y;
                gameResourcesService.expandWildSpine[reel.index].isOpen = true;
                this.addShakeEventAfterWildWin(
                  gameResourcesService.expandWildSpine[reel.index],
                  'wild1',
                  expandingWild,
                  resolve
                );
                gameResourcesService.expandWildSpine[reel.index].addListener(
                  'complete',
                  () => {
                    gameResourcesService.expandWildSpine[reel.index].play('idleBig1', true);
                    resolve();
                  },
                  null,
                  'wild1'
                );
              } else if (symbol.y < (isMobile() ? 152 : 292)) {
                gameResourcesService.expandWildSpine[reel.index].play('wild2', false);
                gameResourcesService.expandWildSpine[reel.index].y = symbol.y;
                gameResourcesService.expandWildSpine[reel.index].isOpen = true;
                this.addShakeEventAfterWildWin(
                  gameResourcesService.expandWildSpine[reel.index],
                  'wild2',
                  expandingWild,
                  resolve
                );
                gameResourcesService.expandWildSpine[reel.index].addListener(
                  'complete',
                  () => {
                    gameResourcesService.expandWildSpine[reel.index].play('idleBig2', true);
                    resolve();
                  },
                  null,
                  'wild2'
                );
              } else if (symbol.y < (isMobile() ? 228 : 438)) {
                gameResourcesService.expandWildSpine[reel.index].play('wild3', false);
                gameResourcesService.expandWildSpine[reel.index].y = symbol.y;
                gameResourcesService.expandWildSpine[reel.index].isOpen = true;
                this.addShakeEventAfterWildWin(
                  gameResourcesService.expandWildSpine[reel.index],
                  'wild3',
                  expandingWild,
                  resolve
                );
                gameResourcesService.expandWildSpine[reel.index].addListener(
                  'complete',
                  () => {
                    gameResourcesService.expandWildSpine[reel.index].play('idleBig3', true);
                    resolve();
                  },
                  null,
                  'wild3'
                );
              } else {
                gameResourcesService.expandWildSpine[reel.index].play('wild4', false);
                gameResourcesService.expandWildSpine[reel.index].y = symbol.y;
                gameResourcesService.expandWildSpine[reel.index].isOpen = true;
                this.addShakeEventAfterWildWin(
                  gameResourcesService.expandWildSpine[reel.index],
                  'wild4',
                  expandingWild,
                  resolve
                );
                gameResourcesService.expandWildSpine[reel.index].addListener(
                  'complete',
                  () => {
                    gameResourcesService.expandWildSpine[reel.index].play('idleBig4', true);
                    resolve();
                  },
                  null,
                  'wild4'
                );
              }
            }
            soundService.play(SoundTypes.WildOpeningSound);
          });
        }
      });
    });
  }

  moveExpandWildContainer() {
    gameResourcesService.expandWildSpine
      .filter((item) => item.isOpen)
      .forEach((item) => {
        item.y += this.columnsMoveSpeed;
      });
  }

  handleJackpotWin() {
    let resolverCount = 0;
    return new Promise((resolve) => {
      this.columns.forEach((reel) => {
        reel._reelIcons.forEach((slotIcon) => {
          if (slotIcon.iconNumber === 400) {
            slotIcon.icon.play('win', true);
            setTimeout(() => {
              slotIcon.icon.addListener(
                'complete',
                () => {
                  resolverCount += 1;
                  if (resolverCount === 3) {
                    resolve(resolverCount);
                  }
                },
                null,
                'win'
              );
            }, [500]);
          }
        });
      });
    });
  }

  handleScatterDrop(reelIndex) {
    this.columns[reelIndex]._reelIcons.forEach((slotIcon) => {
      if (
        slotIcon.iconNumber === iconsIdByName[slotBarSymbolNames.icon10] ||
        slotIcon.iconNumber === iconsIdByName[slotBarSymbolNames.icon11] ||
        slotIcon.iconNumber === iconsIdByName[slotBarSymbolNames.icon9]
      ) {
        slotIcon.icon.play('drop');
        slotIcon.icon.addListener(
          'complete',
          () => {
            slotIcon.icon.play('idle', true);
          },
          null,
          'drop'
        );
      }
    });
  }

  updateColumnsDirection(spinDirection) {
    this.spinDirection = spinDirection;
  }

  execute(spinContainer, winContainer, ticker) {
    this.initGameReels(spinContainer, winContainer, ticker);
    if (!userInfoStore.isReplay) {
      addTouchControl(spinContainer, winContainer, this.columns);
    }
  }
}

export const gameDrawer = new GameDrawer();
